/**
 * Shared Zod primitives for domain-specific event properties.
 *
 * Centralised so the same normalisation rule (e.g. tickers always
 * uppercase, periods are an enum) is enforced everywhere a property
 * with the same name appears. Per ``plans/PLAN_POSTHOG_02_EVENT_CATALOG.md``
 * § "Domain-specific properties" + § "Value normalization rules".
 */

import { z } from "zod";

/** Asset ticker — uppercase normalisation applied at validation time. */
export const TickerProperty = z
  .string()
  .min(1)
  .transform((s) => s.toUpperCase());

/** ISO 3166-1 alpha-2 country code, normalised to uppercase. */
export const CountryCodeProperty = z
  .string()
  .length(2)
  .transform((s) => s.toUpperCase());

/** Period-picker enum — fixed set across the platform. */
export const PeriodProperty = z.enum(["7d", "14d", "30d", "90d"]);

/** Confidence tier enum — mindshare scores. */
export const ConfidenceTierProperty = z.enum([
  "low",
  "medium",
  "high",
  "insufficient",
]);

/** Asset class enum. */
export const AssetClassProperty = z.enum(["stock", "etf", "crypto", "forex"]);

/** Theme axis enum. */
export const ThemeAxisProperty = z.enum(["topic", "industry", "region"]);

/** Sort direction enum. */
export const SortDirectionProperty = z.enum(["asc", "desc"]);

/** Compare-mode enum. */
export const CompareModeProperty = z.enum(["assets", "themes", "sectors"]);

/** Settings-tab enum (admin settings page). */
export const AdminSettingsTabProperty = z.enum([
  "company",
  "security",
  "plan",
  "legal",
]);

/** Login method enum. */
export const LoginMethodProperty = z.enum(["password", "microsoft_sso"]);

/** Account-settings changed field enum. */
export const AccountSettingsFieldProperty = z.enum(["timezone", "locale"]);

/** Non-negative integer (counts, positions). */
export const NonNegativeIntProperty = z.number().int().nonnegative();

// ── Retail surface (/retail) primitives ───────────────────
//
// The /retail IA (Market Pulse / Profile / Radar / Fit / Assets) shares
// one market-scope state (``components/retail/scope-context.tsx``): a
// market code, a look-back window, and a coarse asset-class filter.
// These enums mirror ``SCOPE_ORDER`` / ``ScopeWindow`` / ``ScopeAssetClass``
// so a catalog event can never carry a scope value the UI can't produce.

/** Retail market scope — LATAM pooled region + the entitled countries. */
export const RetailScopeProperty = z.enum(["LATAM", "MX", "PE", "CL", "CO"]);

/** Retail look-back window, in days. */
export const RetailWindowProperty = z.union([z.literal(7), z.literal(30)]);

/** Retail asset-class scope filter (coarser than AssetClassProperty). */
export const RetailAssetClassProperty = z.enum(["all", "stock", "etf"]);
